import * as PIXI from "pixi.js";

import { EGameState, TInitialResponse } from "@src/server/ResponseTypes";
import { TGameAssetAlias } from "@src/game/screens/GameScreen";
import { SymbolReel } from "@src/game/symbol-panel/SymbolReel";
import { EventSystem } from "@src/core/events/EventSystem";
import { IView } from "@src/core/game/components/IView";
import { Symbol } from "@src/game/symbol-panel/Symbol";
import { Assets } from "@src/core/assets/Assets";
import { Spine } from "@pixi/spine-pixi";
import { gameEngine } from "@src/game/GameEngine";
import { constants } from "@src/game/Constants";
import gsap from "gsap";
import { Audio } from "@src/core/audio/Audio";
import { TGameUIButtonType } from "../ui/GameUI";

// TODO: Rename to ReelPanel

// Displays symbol panel, creates reels, spins and stops them based on game events.
export class SymbolPanel implements IView {
  private _view: PIXI.Container = new PIXI.Container();
  public get view(): PIXI.Container {
    return this._view;
  }

  private _symbolReels!: SymbolReel[];
  private _symbolPanelMaskGraphics!: PIXI.Graphics;
  private _symbolPanelBackgroundGraphics!: PIXI.Graphics;

  private _panel!: Spine;
  private _panelBackground!: PIXI.Sprite;

  private _animationTween!: GSAPTween;

  private _showPanelTargetY: number = 0;
  private _hasRunHideAnimation: boolean = false;
  private _hasHiddenEventFired: boolean = false;
  private _panelLevel: "low" | "medium" | "full" = "low";
  private _landedSymbolCount: number = 0;

  public constructor() {
    this._symbolReels = [];

    this._setupEventListeners();

    this._initializeAssets();
    this._parentAssets();

    this._initializeComponents();
    this._parentComponents();
  }

  // Smoothly brings the panel back to its regular position
  public playShowAnimation(): void {
    if (!this._hasRunHideAnimation) {
      return;
    }

    this._animationTween?.kill();
    this._hasHiddenEventFired = false;
    this._view.y = 1600;
    this._view.visible = true;

    this._animationTween = gsap.to(this._view, {
      // TODO: Use the actual target value.
      y: this._showPanelTargetY,
      duration: 0.4,
      ease: "back.out(1)",
      onStart: () => {
        EventSystem.invoke(
          constants.GameEvents.SYMBOL_PANEL_SHOW_ANIMATION_STARTED,
        );
      },
      onComplete: () => {
        this._handleShowAnimationCompletion();
      },
      onInterrupt: () => {
        this._handleShowAnimationCompletion();
      },
    });
  }

  // Smoothly lowers the panel to below-screen
  public playHideAnimation(): void {
    this._hasRunHideAnimation = true;

    this._animationTween?.kill();

    this._symbolReels.forEach((reel) => {
      reel.view.mask = null;
      reel.expandedWildVisualizerView.mask = null;
    });

    this._animationTween = gsap.to(this._view, {
      y: 1600,
      duration: 0.4,
      ease: "back.in(1)",
      onStart: () => {
        EventSystem.invoke(
          constants.GameEvents.SYMBOL_PANEL_HIDE_ANIMATION_STARTED,
        );
        console.log("SYMBOL PANEL HIDE ANIMATION STARTED");
      },
      onComplete: () => {
        this._handleHideAnimationCompletion();
      },
      onInterrupt: () => {
        this._handleHideAnimationCompletion();
      },
    });
  }

  private _handleShowAnimationCompletion(): void {
    console.log("SYMBOL PANEL SHOWN");
    EventSystem.invoke(constants.GameEvents.SYMBOL_PANEL_SHOWN);

    this._hasRunHideAnimation = false;
    this._symbolReels.forEach((reel) => {
      reel.view.mask = this._symbolPanelMaskGraphics;
      reel.expandedWildVisualizerView.mask = this._symbolPanelMaskGraphics;
    });
  }

  private _handleHideAnimationCompletion(): void {
    if (!this._hasHiddenEventFired) {
      this._hasHiddenEventFired = true;
      EventSystem.invoke(constants.GameEvents.SYMBOL_PANEL_HIDDEN);
      console.log("SYMBOL PANEL HIDDEN");
      this._view.visible = false;
    }
  }

  private _onClickedUIButton(buttonType: TGameUIButtonType): void {
    switch (buttonType) {
      case "spin":
        this._symbolPanelBackgroundGraphics.alpha = 0;
        break;

    }
  }

  private _onClickedRigButton(): void {
    this._symbolPanelBackgroundGraphics.alpha = 0.0;
  }

  private _onHighlightSpecificWin(): void {
    // Ensure background stays dimmed while sequential wins are cycling
    this._symbolPanelBackgroundGraphics.alpha = 0.8;
  }

  private _setupEventListeners(): void {
    EventSystem.addListener(
      constants.UIEvents.SELECTED_AUTOPLAY_COUNT,
      this._onSelectedAutoplayCount.bind(this),
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_RIG_BUTTON,
      this._onClickedRigButton.bind(this)
    );

    EventSystem.addListener(
      constants.GameEvents.HIGHLIGHT_SPECIFIC_WIN,
      this._onHighlightSpecificWin.bind(this)
    );

    EventSystem.addListener(
      constants.UIEvents.CLICKED_UI_BUTTON,
      this._onClickedUIButton.bind(this)
    );

    EventSystem.addListener(
      constants.GameEvents.LANDSCAPE_LAYOUT_REQUESTED,
      this._onLandscapeLayoutRequest.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.PORTRAIT_LAYOUT_REQUESTED,
      this._onPortraitLayoutRequest.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.GAME_INITIALIZE,
      this._onInitializedGame.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.REEL_PANEL_SPIN_STOPPED,
      this._onSpinStopped.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_PANEL_SHOW_ANIMATION_STARTED,
      this._onSymbolPanelShowAnimationStarted.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.SYMBOL_LANDED,
      this._onSymbolLanded.bind(this),
    );

    EventSystem.addListener(
      constants.GameEvents.COIN_SPLASH_STOPPED,
      this._onCoinSplashStopped.bind(this),
    );
  }

  private _onInitializedGame(_: TInitialResponse): void {
    this._createSymbolReels();
  }

  private _onSymbolPanelShowAnimationStarted(): void {
    if (gameEngine.gameActionData.state === EGameState.HOLD_TO_MAIN) {
      this._panelLevel = "low";
      this._updatePanelLevel();
    }
  }

  private _onSpinStopped(): void {
    const scatterCount: number =
      gameEngine.gameActionData.scatters?.coordinates.length ?? 0;

    if (gameEngine.gameActionData.wins.length > 0 || scatterCount > 2) {
      this._symbolPanelBackgroundGraphics.alpha = 0.8;
      // TODO: This is a temporary measure, fire an actual event
      // when the win animations stop playing and do this there.

      gsap.delayedCall(1, () => {
        this._symbolPanelBackgroundGraphics.alpha = 0;
      });
    }
  }

  private _initializeAssets(): void {
    this._panel = Spine.from({
      skeleton: "mainPanelAtlasJson",
      atlas: "mainPanelAtlas",
    });
    this._panel.skeleton.setSkinByName("default");
    this._panel.state.setAnimation(0, "idle_1", true);
    this._panel.zIndex = 1;
    this._panel.scale.set(0.195);
    this._panel.position.set(-8, 128);

    this._panelBackground = new PIXI.Sprite(
      Assets.getAsset<TGameAssetAlias>("mainPanelBgImg"),
    );
    this._panelBackground.anchor.set(0.5);
    this._panelBackground.zIndex = 0;
    this._panelBackground.scale.set(0.32);
    this._panelBackground.position.set(-8, 150);
  }

  private _parentAssets(): void {
    this._view.addChild(this._panel);
    this._view.addChild(this._panelBackground);
  }

  private _initializeComponents(): void {
    this._symbolPanelMaskGraphics = new PIXI.Graphics();
    this._symbolPanelMaskGraphics
      .rect(-330, -120, 660, 545)
      .fill({ color: 0xff0000 });

    this._symbolPanelMaskGraphics.alpha = 0;

    this._symbolPanelBackgroundGraphics = new PIXI.Graphics();
    this._symbolPanelBackgroundGraphics
      .rect(-330, -120, 660, 545)
      .fill({ color: 0x000000 });
    this._symbolPanelBackgroundGraphics.alpha = 0;
    this._symbolPanelBackgroundGraphics.zIndex = 0;
  }

  private _parentComponents(): void {
    this._view.addChild(this._symbolPanelMaskGraphics);
    this._view.addChild(this._symbolPanelBackgroundGraphics);
  }

  private _onCoinSplashStopped() {
    if (this._landedSymbolCount > 12 && this._panelLevel !== "full") {
      this._panelLevel = "full";
      this._updatePanelLevel();
    } else if (
      this._landedSymbolCount > 6 &&
      this._panelLevel !== "medium" &&
      this._panelLevel !== "full"
    ) {
      this._panelLevel = "medium";
      this._updatePanelLevel();
    }
  }

  private async _onSelectedAutoplayCount(count: number | "∞"): Promise<void> {
    this._symbolPanelBackgroundGraphics.alpha = 0.0;
  }

  private _onSymbolLanded(type: string, x: number, y: number): void {
    if (type.startsWith("100")) {
      this._landedSymbolCount++;
    }
  }

  // Serves to change panel visuals depending on how many coins
  // we collected throughout the game.
  // TODO: Reset panel level after or before entering hold&win round.
  private _updatePanelLevel(): void {
    switch (this._panelLevel) {
      case "low":
        this._panel.state.setAnimation(0, "idle_1", true);
        Audio.play("sndJackpotLandMidi");
        break;
      case "medium":
        this._panel.state.setAnimation(0, "transition_2", false);
        this._panel.state.addAnimation(0, "idle_2", true);
        Audio.play("sndJackpotLandMajor");
        break;
      case "full":
        this._panel.state.setAnimation(0, "transition_3", false);
        this._panel.state.addAnimation(0, "idle_3", true);
        Audio.play("sndJackpotLandGrand");
        break;
    }
  }

  private _createSymbolReels(): void {
    for (let i = 0; i < gameEngine.config.reels; i++) {
      const reel: SymbolReel = new SymbolReel(i);

      const positionX: number =
        i * Symbol.WIDTH -
        (gameEngine.config.reels * Symbol.WIDTH) / 2 +
        Symbol.WIDTH / 2;

      reel.view.x = positionX;
      reel.view.y = SymbolReel.WORLD_Y_OFFSET;

      reel.anticipationSkeletonView.x = positionX;
      reel.anticipationSkeletonView.y = SymbolReel.WORLD_Y_OFFSET;

      reel.expandedWildVisualizerView.x = positionX;
      reel.expandedWildVisualizerView.y = SymbolReel.WORLD_Y_OFFSET;

      reel.view.mask = this._symbolPanelMaskGraphics;
      reel.expandedWildVisualizerView.mask = this._symbolPanelMaskGraphics;

      this._view.addChild(reel.view);
      this._view.addChild(reel.anticipationSkeletonView);
      this._view.addChild(reel.expandedWildVisualizerView);
      this._symbolReels.push(reel);
    }
  }

  private _onLandscapeLayoutRequest(): void {
    // This value is based on the position SymbolPanel is set to
    // in GameScreen.ts during screen resize event.
    this._showPanelTargetY = -120;
  }

  private _onPortraitLayoutRequest(): void {
    // This value is based on the position SymbolPanel is set to
    // in GameScreen.ts during screen resize event.
    this._showPanelTargetY = -310;
  }
}
